import { ok, technical, type Result } from './result'
import { UPLOAD_ACCEPT, isAllowedType } from './fileTypes'

export type AssembledPdf = { bytes: Uint8Array; pageCount: number }
export type SplitPdfs = { avgs: Blob; tb: Blob; totalPages: number }

const A4 = [595.28, 841.89] as const

async function upright(file: File, type: string): Promise<Uint8Array> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  const canvas = new OffscreenCanvas(bitmap.width, bitmap.height)
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0)
  return new Uint8Array(await (await canvas.convertToBlob({ type, quality: 0.92 })).arrayBuffer())
}

function looksEncrypted(bytes: Uint8Array): boolean {
  return new TextDecoder('latin1').decode(bytes).includes('/Encrypt')
}

export async function assemblePages(
  files: readonly File[],
  { fitA4 = false }: { fitA4?: boolean } = {},
): Promise<Result<AssembledPdf>> {
  if (files.length === 0) {
    return technical("Bitte mindestens eine Datei auswählen.")
  }

  const { PDFDocument } = await import('pdf-lib')
  const combined = await PDFDocument.create()

  for (const file of files) {
    if (!isAllowedType(file, UPLOAD_ACCEPT)) {
      return technical(
        `Dateiformat nicht unterstützt (${file.name}) – bitte PDF, JPG oder PNG hochladen.`,
      )
    }
    const bytes = new Uint8Array(await file.arrayBuffer())
    const type = file.type.toLowerCase()

    if (type === 'application/pdf') {
      let source
      try {
        source = await PDFDocument.load(bytes, { ignoreEncryption: true })
      } catch {
        if (looksEncrypted(bytes)) {
          return technical(
            `Die PDF-Datei „${file.name}" ist passwortgeschützt – bitte zuerst entsperren und erneut hochladen.`,
          )
        }
        return technical(`Die Datei „${file.name}" konnte nicht als PDF gelesen werden.`)
      }
      const pages = await combined.copyPages(source, source.getPageIndices())
      pages.forEach((page) => combined.addPage(page))
    } else {
      let image
      try {
        const pixels = fitA4 ? await upright(file, type) : bytes
        image = type === 'image/jpeg' ? await combined.embedJpg(pixels) : await combined.embedPng(pixels)
      } catch (error) {
        if (!fitA4) throw error
        return technical(
          `Die Datei „${file.name}" konnte nicht als Bild gelesen werden. Bitte laden Sie sie als PDF, JPG oder PNG erneut hoch.`,
        )
      }
      const { width, height } = image
      const scale = width <= height ? Math.min(A4[0] / width, A4[1] / height) : Math.min(A4[1] / width, A4[0] / height)
      const [w, h] = fitA4 ? [width * scale, height * scale] : [width, height]
      combined.addPage([w, h]).drawImage(image, { x: 0, y: 0, width: w, height: h })
    }
  }

  const bytes = await combined.save()
  return ok({ bytes, pageCount: combined.getPageCount() })
}

export async function splitAt(assembled: AssembledPdf, avgsPageIndex: number): Promise<Result<SplitPdfs>> {
  if (assembled.pageCount < 2) {
    return technical(
      "Seite 1 muss der AVGS-Gutschein sein, ab Seite 2 die Trägerbestätigung – dafür werden mindestens 2 Seiten benötigt.",
    )
  }
  if (avgsPageIndex < 0 || avgsPageIndex >= assembled.pageCount) {
    return technical(
      `Seite ${avgsPageIndex + 1} gibt es in diesem Dokument nicht (nur ${assembled.pageCount} Seiten vorhanden).`,
    )
  }

  const { PDFDocument } = await import('pdf-lib')
  const source = await PDFDocument.load(assembled.bytes)

  const avgsDoc = await PDFDocument.create()
  const [avgsPage] = await avgsDoc.copyPages(source, [avgsPageIndex])
  avgsDoc.addPage(avgsPage)

  const tbDoc = await PDFDocument.create()
  const tbIndices = source.getPageIndices().filter((i) => i !== avgsPageIndex)
  const tbPages = await tbDoc.copyPages(source, tbIndices)
  tbPages.forEach((page) => tbDoc.addPage(page))

  const [avgsBytes, tbBytes] = await Promise.all([avgsDoc.save(), tbDoc.save()])

  return ok({
    avgs: new Blob([new Uint8Array(avgsBytes)], { type: 'application/pdf' }),
    tb: new Blob([new Uint8Array(tbBytes)], { type: 'application/pdf' }),
    totalPages: assembled.pageCount,
  })
}

export async function copyPages(assembled: AssembledPdf, indices: readonly number[]): Promise<Result<Blob>> {
  const bad = indices.find((i) => i < 0 || i >= assembled.pageCount)
  if (bad !== undefined) {
    return technical(`Seite ${bad + 1} gibt es in diesem Dokument nicht (nur ${assembled.pageCount} Seiten vorhanden).`)
  }

  const { PDFDocument } = await import('pdf-lib')
  const source = await PDFDocument.load(assembled.bytes)

  const doc = await PDFDocument.create()
  const pages = await doc.copyPages(source, [...indices])
  pages.forEach((page) => doc.addPage(page))

  const bytes = await doc.save()
  return ok(new Blob([new Uint8Array(bytes)], { type: 'application/pdf' }))
}

export async function splitWithPlan(
  assembled: AssembledPdf,
  avgsPageIndicesInOrder: readonly number[],
  tbPageIndicesInOrder: readonly number[],
): Promise<Result<SplitPdfs>> {
  if (tbPageIndicesInOrder.length === 0) {
    return technical("splitWithPlan benötigt mindestens eine Trägerbestätigungs-Seite.")
  }

  const avgsResult = await copyPages(assembled, avgsPageIndicesInOrder)
  if (!avgsResult.ok) return avgsResult
  const tbResult = await copyPages(assembled, tbPageIndicesInOrder)
  if (!tbResult.ok) return tbResult

  return ok({
    avgs: avgsResult.data,
    tb: tbResult.data,
    totalPages: assembled.pageCount,
  })
}

export async function assembledFromBytes(bytes: Uint8Array): Promise<AssembledPdf> {
  const { PDFDocument } = await import('pdf-lib')
  const doc = await PDFDocument.load(bytes)
  return { bytes, pageCount: doc.getPageCount() }
}
